import { Link, usePage } from "@inertiajs/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Bucket, SharedProps } from "@/types";
import { useTheme } from "@/stores/theme";
import { useSidebar } from "@/stores/sidebar";
import { useDndMonitor, useDroppable, type DragEndEvent } from "@dnd-kit/core";
import SidebarQuickAdd from "./SidebarQuickAdd";
import LotusIcon from "./LotusIcon";
import { todayIso } from "@/lib/dates";
import { signOut } from "@/lib/signOut";
import { flushOutbox } from "@/lib/syncEngine";
import { changeCount, pluralRu } from "@/lib/plural";
import { unsentCount, useTaskStore } from "@/stores/taskStore";
import OnlineLink from "@/components/OnlineLink";
import DeleteConfirm from "@/components/DeleteConfirm";
import { NEEDS_NETWORK, useOnline } from "@/hooks/useOnline";
import { reloadApp } from "@/lib/swClient";
import { bulkPatchTasks, updateTask } from "@/mutations/tasks";
import { useLocalView } from "@/hooks/useLocalView";
import { sidebarCounts, type ViewSource } from "@/lib/views";

const SUN = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const CALENDAR = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const TASKS = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const TEMPLATES = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const SIGN_OUT = (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
  >
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);

const RELOAD = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const MOON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

const SUN_SMALL = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const SIDEBAR_TOGGLE = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6z" />
    <path d="M9 4v16" />
  </svg>
);

const GEAR = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const LATER = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

type ActiveData =
  | { kind: "task"; taskId: string; isRoot: boolean; currentListId?: string; groupIds?: string[] };

type OverData =
  | { kind: "list"; listId: string }
  | { kind: "smart-myday" };

function MyDayDroppable({ children }: { children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: "smart-myday", data: { kind: "smart-myday" } });
  return (
    <div ref={setNodeRef} className={isOver ? "drop-target-active" : ""}>
      {children}
    </div>
  );
}

function SidebarCount({ count, dots, className = "" }: { count: number; dots: boolean; className?: string }) {
  if (!dots) {
    return <span className={`${className} text-xs text-gray-400 dark:text-gray-500 tabular-nums`}>{count}</span>;
  }
  if (count === 0) return null;
  return (
    <span className={`${className} flex items-center`} role="img" aria-label="Есть задачи">
      <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
    </span>
  );
}

function BucketLink({ bucket, icon, active, dots }: { bucket: Bucket; icon: React.ReactNode; active: boolean; dots: boolean }) {
  const href = `/lists/${bucket.id}`;
  const { setNodeRef, isOver } = useDroppable({ id: `list:${bucket.id}`, data: { kind: "list", listId: bucket.id } });
  return (
    <div ref={setNodeRef} className={isOver ? "drop-target-active" : ""}>
      <div className="sidebar-quick-add-host">
        <Link href={href} className={`sidebar-item ${active ? "active" : ""}`} prefetch={["hover", "click"]} cacheFor="30s">
          {icon}
          <span className="flex-1">{bucket.name}</span>
          <SidebarCount count={bucket.count} dots={dots} className="sidebar-count" />
        </Link>
        <SidebarQuickAdd listId={bucket.id} href={href} name={bucket.name} />
      </div>
    </div>
  );
}

const UNSENT_FORMS = ["не отправлено", "не отправлены", "не отправлены"] as const;

export default function Sidebar() {
  const page = usePage<SharedProps>();
  const fedSidebar = page.props.sidebar;
  const countDots = fedSidebar?.countDots ?? false;
  const selectCounts = useCallback((source: ViewSource) => sidebarCounts(source, countDots), [countDots]);
  const sidebar = useLocalView("sidebar", selectCounts) ?? fedSidebar;
  const currentUrl = page.url;
  const [unsentAtSignOut, setUnsentAtSignOut] = useState<number | null>(null);
  const [sendingBeforeSignOut, setSendingBeforeSignOut] = useState(false);
  const online = useOnline();

  const { theme, toggle: toggleTheme } = useTheme();
  const { mobileOpen, close: closeMobile, desktopCollapsed: collapsed, toggleCollapsed } = useSidebar();
  const wasDraggingRef = useRef(false);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!wasDraggingRef.current) return;
      // Swallows the click a browser synthesizes right after a drop, before a Link or button sees it.
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      wasDraggingRef.current = false;
    };
    // A touch drop synthesizes no click, so the next gesture must not inherit the flag.
    const reset = () => {
      wasDraggingRef.current = false;
    };
    document.addEventListener("click", handler, true);
    document.addEventListener("pointerdown", reset, true);
    return () => {
      document.removeEventListener("click", handler, true);
      document.removeEventListener("pointerdown", reset, true);
    };
  }, []);

  useDndMonitor({
    onDragStart: () => {
      wasDraggingRef.current = true;
    },
    onDragEnd: (e: DragEndEvent) => {
      const active = e.active.data.current as ActiveData | undefined;
      const over = e.over?.data.current as OverData | undefined;
      if (!active || !over) return;

      if (active.kind !== "task") return;

      const groupIds = active.groupIds;
      if (over.kind === "smart-myday") {
        if (groupIds) bulkPatchTasks(groupIds, { my_day_on: todayIso() });
        else updateTask(active.taskId, { my_day_on: todayIso() });
        return;
      }

      if (over.kind !== "list") return;
      // A subtask dropped on a bucket is promoted into it (Task#move_to_list!) — even its own
      // bucket, because leaving the parent is the whole point of the gesture.
      if (groupIds) {
        bulkPatchTasks(groupIds, { list_id: over.listId });
        return;
      }
      if (active.isRoot && over.listId === active.currentListId) return;
      updateTask(active.taskId, { list_id: over.listId });
    },
  });

  if (!sidebar) return null;

  const isActive = (path: string) => currentUrl === path || currentUrl.startsWith(path + "?");

  async function requestSignOut() {
    if (useTaskStore.getState().pending.length > 0) {
      setSendingBeforeSignOut(true);
      try {
        await flushOutbox();
      } finally {
        setSendingBeforeSignOut(false);
      }
    }
    const unsent = unsentCount(useTaskStore.getState());
    if (unsent > 0) setUnsentAtSignOut(unsent);
    else signOut();
  }

  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}>
      <div className="flex-1 p-3 sidebar-body">
        <div className="flex items-center justify-between mb-6 px-1 pt-1">
          <span className="text-sm font-semibold tracking-tight text-gray-900 dark:text-white">
            revolver
          </span>
          <button
            onClick={closeMobile}
            className="md:hidden p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Закрыть панель"
            aria-label="Закрыть панель"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4 space-y-1">
          <MyDayDroppable>
            <div className="relative">
              <Link
                href="/my-day"
                className={`sidebar-item ${isActive("/my-day") ? "active" : ""}`}
                prefetch={["hover", "click"]}
                cacheFor="30s"
              >
                {SUN}
                <span className="flex-1">Мой день</span>
                <SidebarCount count={sidebar.smartLists.find((s) => s.kind === "myDay")?.count ?? 0} dots={countDots} />
              </Link>
              <Link
                href="/zen?my_day=1"
                className="sidebar-zen"
                title="Дзен-режим"
                aria-label="Дзен-режим"
              >
                <LotusIcon className="w-4 h-4" />
              </Link>
            </div>
          </MyDayDroppable>
          <Link
            href="/planned"
            className={`sidebar-item ${isActive("/planned") ? "active" : ""}`}
            prefetch={["hover", "click"]}
            cacheFor="30s"
          >
            {CALENDAR}
            <span className="flex-1">Запланировано</span>
            <SidebarCount count={sidebar.smartLists.find((s) => s.kind === "planned")?.count ?? 0} dots={countDots} />
          </Link>
          {sidebar.inbox && (
            <BucketLink bucket={sidebar.inbox} icon={TASKS} active={isActive(`/lists/${sidebar.inbox.id}`)} dots={countDots} />
          )}
        </div>

        <div role="separator" className="border-t border-gray-100 dark:border-gray-800 mb-4" />

        {sidebar.later && (
          <BucketLink bucket={sidebar.later} icon={LATER} active={isActive(`/lists/${sidebar.later.id}`)} dots={countDots} />
        )}
      </div>

      <div className="sidebar-templates px-3 py-2 border-t border-gray-100 dark:border-gray-800">
        <Link
          href="/templates"
          className={`sidebar-item ${isActive("/templates") ? "active" : ""}`}
          prefetch={["hover", "click"]}
          cacheFor="30s"
        >
          {TEMPLATES}
          <span className="flex-1">Шаблоны</span>
          <SidebarCount count={sidebar.smartLists.find((s) => s.kind === "templates")?.count ?? 0} dots={countDots} />
        </Link>
      </div>

      <div className="p-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="sidebar-footer-actions flex items-center gap-1">
          <OnlineLink
            href="/settings/devices"
            className={`p-1.5 cursor-pointer rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 ${
              currentUrl.startsWith("/settings")
                ? "text-gray-700 dark:text-gray-200"
                : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            }`}
            title="Настройки"
            aria-label="Настройки"
          >
            {GEAR}
          </OnlineLink>
          <button
            onClick={toggleTheme}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Переключить тему"
            aria-label="Переключить тему"
          >
            {theme === "dark" ? SUN_SMALL : MOON}
          </button>
          <button
            onClick={() => void reloadApp()}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Обновить приложение"
            aria-label="Обновить приложение"
          >
            {RELOAD}
          </button>
          <button
            onClick={() => void requestSignOut()}
            disabled={!online || sendingBeforeSignOut}
            aria-busy={sendingBeforeSignOut}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
            title={online ? "Выйти из аккаунта" : NEEDS_NETWORK}
            aria-label="Выйти"
          >
            {SIGN_OUT}
          </button>
        </div>
        <button
          onClick={toggleCollapsed}
          className="hidden md:inline-flex p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
          title="Свернуть боковую панель"
          aria-label="Свернуть боковую панель"
        >
          {SIDEBAR_TOGGLE}
        </button>
      </div>

      {unsentAtSignOut !== null &&
        createPortal(
          <DeleteConfirm
            title={`${changeCount(unsentAtSignOut)} ${pluralRu(unsentAtSignOut, UNSENT_FORMS)}. Всё равно выйти?`}
            body="Они останутся на этом устройстве и отправятся, когда вы снова войдёте в этот аккаунт."
            confirmLabel="Выйти"
            onConfirm={() => {
              setUnsentAtSignOut(null);
              signOut();
            }}
            onCancel={() => setUnsentAtSignOut(null)}
          />,
          document.body,
        )}
    </aside>
  );
}
